// CUSTOM:finnstyle-special-variants-setup-v1.0 — entire file is this customization.
// See customizations/0004 for the feature, and 0005 for the full member list.

if (!window.variantPickerAccordionState) {
  window.variantPickerAccordionState = new Map();

  const SCROLL_SELECTOR = '.variant-picker-accordion__grid, .variant-picker-accordion__fieldset--grouped';

  // native .focus() auto-scrolls EVERY scrollable ancestor of the focused element into
  // view, not just the nearest one — including the page/window itself. Track window scroll
  // position the same way as the swatch grid's, independently, since it's a separate
  // scrollable context that the same focus call can also displace.
  let lastWindowScrollY = window.scrollY;
  window.addEventListener('scroll', () => {
    lastWindowScrollY = window.scrollY;
  });
  const restoreWindowScroll = () => {
    window.scrollTo(0, lastWindowScrollY);
    requestAnimationFrame(() => window.scrollTo(0, lastWindowScrollY));
  };

  const keyFor = (details) => {
    const container = details.closest('[data-section]');
    const sectionId = container?.dataset.section;
    const position = details.dataset.optionPosition;
    if (!sectionId || !position) return null;
    return `${sectionId}-${position}`;
  };

  const scrollContainerFor = (details) => details.querySelector(SCROLL_SELECTOR);

  const recordState = (details) => {
    const key = keyFor(details);
    if (!key) return;
    const scrollEl = scrollContainerFor(details);
    window.variantPickerAccordionState.set(key, {
      open: details.open,
      scrollTop: scrollEl ? scrollEl.scrollTop : 0,
    });
  };

  // Native 'toggle' events on <details> don't bubble, but capture-phase listening
  // at the document level still receives them regardless of target depth.
  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target.closest?.('.variant-picker-accordion__item');
      if (details) recordState(details);
    },
    true
  );

  // 'scroll' also doesn't bubble — capture it on the swatch grid itself so scroll
  // position (not just open/closed) survives the DOM swap below.
  document.addEventListener(
    'scroll',
    (event) => {
      if (!event.target.matches?.(SCROLL_SELECTOR)) return;
      const details = event.target.closest('.variant-picker-accordion__item');
      if (details) recordState(details);
    },
    true
  );

  const restoreScrollTop = (scrollEl, state) => {
    if (!scrollEl || !state?.scrollTop) return;
    scrollEl.scrollTop = state.scrollTop;
    requestAnimationFrame(() => {
      scrollEl.scrollTop = state.scrollTop;
    });
  };

  // show-more.js toggles a plain .hidden class on each group's overflow slice — also pure
  // live-DOM state, wiped out by the same full-node swap. The button's own id is already
  // deterministic per group (Show-More-{section}-{option position}-{group index|"other"}),
  // so it doubles as a stable state key with no extra markup needed.
  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('.button-show-more');
    if (!button?.id) return;
    // ShowMoreButton's own listener is attached directly to this button, so it has already
    // run (at-target phase) by the time this document-level bubble listener fires.
    const groupEl = button.closest('.parent-display');
    const isExpanded = !groupEl?.querySelector('.show-more-item')?.classList.contains('hidden');
    window.variantPickerAccordionState.set(`show-more-${button.id}`, isExpanded);
  });

  // Collapsed-header indicator dots. Rather than re-deriving each swatch's colour/image in
  // Liquid — the theme resolves them three different ways depending on picker type, and the
  // legacy 'color' path this store uses runs ~30 lines of settings.swatch_list and
  // handle-based image lookups — just clone the inline style off the swatches the theme has
  // already rendered in the accordion body. They are the same values by construction, so the
  // dots can never drift from the real swatches, whichever path produced them. The body is in
  // the DOM even while the <details> is closed, so nothing has to be opened first.
  const buildIndicatorDots = () => {
    document.querySelectorAll('.variant-picker-accordion__dots').forEach((target) => {
      const details = target.closest('.variant-picker-accordion__item');
      const swatches = details?.querySelectorAll('.accordion-details__content .swatch-color');
      if (!swatches?.length) return;

      // Sample evenly across the whole list rather than taking the first N, so the dots show
      // the range of available colours. Deterministic on purpose — true randomness would
      // reshuffle the dots on every variant swap, which reads as flicker.
      const max = parseInt(target.dataset.dotCount, 10) || 7;
      const step = Math.max(1, Math.floor(swatches.length / max));
      const picked = [];
      for (let i = 0; i < swatches.length && picked.length < max; i += step) {
        picked.push(swatches[i]);
      }

      target.replaceChildren(
        ...picked.map((swatch) => {
          const dot = document.createElement('span');
          dot.className = 'swatch-color variant-picker-accordion__dot';
          dot.setAttribute('style', swatch.getAttribute('style') || '');
          return dot;
        })
      );
    });
  };

  buildIndicatorDots();

  // product-info.js replaces the whole <variant-selects> node (and everything inside it)
  // on every option change, so freshly-inserted <details> always start closed with no
  // scroll history. Restore whatever was open, and wherever it was scrolled to, once
  // that swap has completed.
  document.addEventListener('variant:changed', () => {
    document.querySelectorAll('.variant-picker-accordion__item').forEach((details) => {
      const key = keyFor(details);
      if (!key) return;
      const state = window.variantPickerAccordionState.get(key);
      if (!state) return;

      if (state.open && !details.open) {
        // Bypass the animated `.open` setter (which always replays the expand transition)
        // by setting the same state AccordionDetails' own constructor uses for an
        // already-open page load: instant, no animation.
        details.isOpen = true;
        details.setAttribute('open', '');
        details.setAttribute('aria-expanded', 'true');
      }

      restoreScrollTop(scrollContainerFor(details), state);
    });

    document.querySelectorAll('.button-show-more').forEach((button) => {
      if (!button.id) return;
      if (!window.variantPickerAccordionState.get(`show-more-${button.id}`)) return;
      const groupEl = button.closest('.parent-display');
      groupEl?.querySelectorAll('.show-more-item').forEach((item) => item.classList.remove('hidden'));
      button.querySelector('.label-show-more')?.classList.add('hidden');
      button.querySelector('.label-show-less')?.classList.remove('hidden');
    });

    // The swap replaces the summary too, so the dot containers come back empty.
    buildIndicatorDots();

    restoreWindowScroll();
  });

  // product-info.js focuses the just-selected input after the swap (to restore keyboard
  // position), and native .focus() auto-scrolls its nearest scrollable ancestor into view
  // unless { preventScroll: true } is passed — which it isn't. That happens as a separate,
  // later step than the variant:changed restore above, so it can still displace the scroll
  // position afterwards. React directly to focus landing in one of our containers (this also
  // fires harmlessly on the user's original click, before any swap, re-asserting where they
  // already are) and reassert the correct position every time, rather than relying on a fixed
  // ordering between the swap's restore and this focus call.
  document.addEventListener('focusin', (event) => {
    if (!event.target.closest?.('.variant-picker-accordion')) return;
    restoreWindowScroll();

    const scrollEl = event.target.closest?.(SCROLL_SELECTOR);
    if (!scrollEl) return;
    const details = scrollEl.closest('.variant-picker-accordion__item');
    const key = details && keyFor(details);
    const state = key && window.variantPickerAccordionState.get(key);
    if (state) restoreScrollTop(scrollEl, state);
  });

  // The theme's swatch tooltip (assets/theme.css .tooltip) is position:absolute, floating
  // above its label via inset-block-end. Our scroll containers' overflow-y:auto/overflow-x:
  // hidden (needed for the row-cap scroll and to suppress a stray horizontal scrollbar) clip
  // anything that renders outside their own box — including that tooltip, for any swatch in
  // the box's first visible row (Wood Finish's single row is always "first row", so its
  // tooltip is always fully clipped). Escape this by switching only these tooltips to
  // position:fixed while hovered — position:fixed isn't constrained by ancestor overflow.
  // transform/opacity are left untouched so the existing hover fade/scale animation keeps
  // working purely via the unmodified :hover CSS rule, and the gap above the swatch comes
  // from CSS (.tooltip margin-block-end in component-variant-picker-accordion.css) rather
  // than a magic number here — so inset-block-end below is simply the swatch's top edge.
  //
  // NOTE: this only works because component-variant-picker-accordion.css forces
  // transform:none on .variant-picker-accordion__content. The theme's accordion open
  // animation (AccordionDetails.animateAccordion, theme.js) leaves an inline
  // transform:translateY(0) committed on that element, and any non-none transform makes it
  // the containing block for position:fixed descendants — which would silently resolve these
  // viewport coordinates against that div instead of the viewport. See the CSS for details.
  //
  // Built as two fully-qualified alternatives, not a template-string concat of
  // SCROLL_SELECTOR + " [data-tooltip]" — that would silently split on the comma inside
  // SCROLL_SELECTOR into ".variant-picker-accordion__grid" (matches the whole grid, no
  // [data-tooltip] required) and ".variant-picker-accordion__fieldset--grouped [data-tooltip]",
  // which only works for grouped mode and always resolves flat-mode hovers to the grid itself.
  const TOOLTIP_ANCHOR_SELECTOR =
    '.variant-picker-accordion__grid [data-tooltip], .variant-picker-accordion__fieldset--grouped [data-tooltip]';

  // These coordinates are deliberately left in place after the pointer moves away, rather than
  // being cleared. Clearing them mid-fade snaps the tooltip back to its native absolute
  // placement — a 1.5rem jump up (the theme's inset-block-end offset, which the CSS above
  // neutralises for the hovered state) right as the opacity transition starts, and it also
  // puts the tooltip back inside the scroll container's clipping, cutting off the fade-out for
  // first-row swatches. Keeping the coordinates lets each tooltip fade out exactly where it
  // faded in. Harmless to leave: an unhovered tooltip is opacity:0 and pointer-events-none, and
  // being out of flow it occupies no space; every hover recomputes fresh coordinates, and the
  // variant DOM swap replaces these nodes wholesale, so nothing stale accumulates.
  document.addEventListener('mouseover', (event) => {
    const anchor = event.target.closest?.(TOOLTIP_ANCHOR_SELECTOR);
    const tooltip = anchor?.querySelector('.tooltip');
    if (!tooltip) return;

    const rect = anchor.getBoundingClientRect();
    tooltip.style.position = 'fixed';
    tooltip.style.insetInlineStart = `${rect.left + rect.width / 2}px`;
    tooltip.style.insetInlineEnd = 'auto';
    tooltip.style.insetBlockStart = 'auto';
    tooltip.style.insetBlockEnd = `${window.innerHeight - rect.top}px`;
  });
}
